import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Input } from '~/components/ui';
import { CurrencySelector } from '~/components/ui/inputs/CurrencySelector';
import { MIN_POSITIVE_PRICE, MAX_PRICE } from '~/utils/moneyBounds';
import type { Currency } from '~/graphql/graphql';
import type { ProductGroup } from '~/pages/rfq-view/itemGroups';
import {
  parsePrice,
  quoteResponseResetKey,
  submissionErrorKey,
  useQuoteResponseDraft,
  type QuoteResponseSeed,
} from '~/pages/quote-response/useQuoteResponseDraft';
import { itemsByPlatformProductLengthId, moneyOrDash } from './quoteDisplay';
import type { CounterableQuote } from './quoteNegotiation';
import { useSendCounter, type CounterSubmission } from './useSendCounter';

type CounterQuoteActionProps = Readonly<{
  quote: CounterableQuote;
  groups: ProductGroup[];
  usesWholeRequestPrice: boolean;
  requestForQuoteCurrency: Currency | null | undefined;
}>;

export function CounterQuoteAction({
  quote,
  groups,
  usesWholeRequestPrice,
  requestForQuoteCurrency,
}: CounterQuoteActionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const sendCounter = useSendCounter(quote.id, () => setIsOpen(false));

  return (
    <div className="flex flex-col gap-2">
      <CounterQuoteButton
        quoteId={quote.id}
        isOpen={isOpen}
        onToggle={() => setIsOpen((current) => !current)}
      />
      {/* Mounted only while open, so each opening starts from the quote's latest terms. */}
      {isOpen && (
        <CounterQuoteForm
          quote={quote}
          groups={groups}
          usesWholeRequestPrice={usesWholeRequestPrice}
          requestForQuoteCurrency={requestForQuoteCurrency}
          submitCounter={sendCounter}
        />
      )}
    </div>
  );
}

export function CounterQuoteButton({
  quoteId,
  isOpen,
  onToggle,
}: Readonly<{ quoteId: string; isOpen: boolean; onToggle: () => void }>) {
  const { t } = useTranslation('rfq-view');

  return (
    <Button
      variant="secondary"
      size="sm"
      width="auto"
      data-testid={`counter-quote-${quoteId}`}
      aria-expanded={isOpen}
      onClick={onToggle}
    >
      {t('rfq_view.counter_quote')}
    </Button>
  );
}

export function CounterQuoteForm({
  quote,
  groups,
  usesWholeRequestPrice,
  requestForQuoteCurrency,
  submitCounter,
}: Readonly<{
  quote: CounterableQuote;
  groups: ProductGroup[];
  usesWholeRequestPrice: boolean;
  requestForQuoteCurrency: Currency | null | undefined;
  submitCounter: (submission: CounterSubmission) => Promise<void>;
}>) {
  const { t } = useTranslation('rfq-view');
  const [isSending, setIsSending] = useState(false);

  const seed = useMemo<QuoteResponseSeed>(() => {
    const items = itemsByPlatformProductLengthId(quote.items);
    // Only the lengths this quote holds an item for: the counter carries no quantity, and
    // an override of quantity 0 on any other length is ignored server-side.
    const quotedGroups = groups
      .map((group) => ({
        ...group,
        rows: group.rows.filter((row) =>
          items.has(row.platformProductLengthId)
        ),
      }))
      .filter((group) => group.rows.length > 0);
    return {
      groups: quotedGroups,
      items,
      currency: quote.currency ?? requestForQuoteCurrency ?? null,
      leadTimeDays: quote.leadTimeDays ?? null,
      sellingOrganizationFacilityId:
        quote.sellingOrganizationFacility?.id ?? null,
      wholeRequestPickupPrice: quote.offeredPickupPriceForTally ?? null,
      wholeRequestDeliveryPrice: quote.offeredDeliveryPriceForTally ?? null,
    };
  }, [quote, groups, requestForQuoteCurrency]);

  const { draft, patch, patchRow, itemOverrides } = useQuoteResponseDraft(
    seed,
    quoteResponseResetKey(quote.id, seed.groups, seed.items),
    usesWholeRequestPrice,
    []
  );

  const showsProductName = seed.groups.length > 1;
  const orderedItems = useMemo(
    () =>
      itemsByPlatformProductLengthId(
        quote.items.filter((item) => item.ordered ?? item.order != null)
      ),
    [quote.items]
  );

  const handleConfirm = async () => {
    // The counter keeps the seller's ship-from facility, so it is never checked or sent.
    const errorKey = submissionErrorKey(draft, usesWholeRequestPrice, {
      requiresShipFrom: false,
    });
    if (errorKey) {
      toast.error(t(errorKey));
      return;
    }
    const wholeRequestMoney = (value: string) =>
      usesWholeRequestPrice ? parsePrice(value, draft.currency) : null;

    const submission: CounterSubmission = {
      itemOverrides,
      tallyPrice: {
        useTallyPrice: usesWholeRequestPrice,
        offeredPickupPriceForTally: wholeRequestMoney(
          draft.wholeRequestPickupPrice
        ),
        offeredDeliveryPriceForTally: wholeRequestMoney(
          draft.wholeRequestDeliveryPrice
        ),
        currency: draft.currency,
      },
      leadTimeDays: parseInt(draft.leadTimeDays, 10),
    };

    setIsSending(true);
    try {
      await submitCounter(submission);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 min-w-[220px]">
      {usesWholeRequestPrice ? (
        <div className="flex items-center gap-2">
          <Input
            name={`${quote.id}-counter-whole-request-pickup`}
            aria-label={t('rfq_view.whole_request_pickup_price_label')}
            type="number"
            min={MIN_POSITIVE_PRICE}
            max={MAX_PRICE}
            step="0.01"
            containerClassName="min-w-[90px]"
            value={draft.wholeRequestPickupPrice}
            onChange={(_, value) => patch({ wholeRequestPickupPrice: value })}
          />
          <Input
            name={`${quote.id}-counter-whole-request-delivery`}
            aria-label={t('rfq_view.whole_request_delivery_price_label')}
            type="number"
            min={MIN_POSITIVE_PRICE}
            max={MAX_PRICE}
            step="0.01"
            containerClassName="min-w-[90px]"
            value={draft.wholeRequestDeliveryPrice}
            onChange={(_, value) => patch({ wholeRequestDeliveryPrice: value })}
          />
        </div>
      ) : (
        seed.groups.flatMap((group) =>
          group.rows.map((row) => {
            const rowLabel = showsProductName
              ? `${group.productName} ${row.displayValue}`
              : row.displayValue;
            const orderedItem = orderedItems.get(row.platformProductLengthId);
            if (orderedItem) {
              return (
                <div
                  key={row.platformProductLengthId}
                  className="flex items-center gap-2"
                  data-testid={`${quote.id}-${row.platformProductLengthId}-counter-ordered-line`}
                >
                  <span className="text-xs text-neut-sec whitespace-nowrap">
                    {rowLabel}
                  </span>
                  <span className="text-xs text-neut-prim">
                    {`${moneyOrDash(orderedItem.offeredPickupPrice)} / ${moneyOrDash(orderedItem.offeredDeliveryPrice)}`}
                  </span>
                  <span className="text-xs text-neut-sec">
                    {t('rfq_view.counter_ordered_line')}
                  </span>
                </div>
              );
            }
            const rowDraft = draft.rows.get(row.platformProductLengthId);
            return (
              <div
                key={row.platformProductLengthId}
                className="flex items-center gap-2"
              >
                <span className="text-xs text-neut-sec whitespace-nowrap">
                  {rowLabel}
                </span>
                <Input
                  name={`${quote.id}-${row.platformProductLengthId}-counter-pickup-price`}
                  aria-label={`${t('rfq_view.pickup_price_label')} ${rowLabel}`}
                  type="number"
                  min={MIN_POSITIVE_PRICE}
                  max={MAX_PRICE}
                  step="0.01"
                  containerClassName="min-w-[90px]"
                  value={rowDraft?.pickupPrice ?? ''}
                  onChange={(_, value) =>
                    patchRow(row.platformProductLengthId, {
                      pickupPrice: value,
                    })
                  }
                />
                <Input
                  name={`${quote.id}-${row.platformProductLengthId}-counter-delivery-price`}
                  aria-label={`${t('rfq_view.delivery_price_label')} ${rowLabel}`}
                  type="number"
                  min={MIN_POSITIVE_PRICE}
                  max={MAX_PRICE}
                  step="0.01"
                  containerClassName="min-w-[90px]"
                  value={rowDraft?.deliveryPrice ?? ''}
                  onChange={(_, value) =>
                    patchRow(row.platformProductLengthId, {
                      deliveryPrice: value,
                    })
                  }
                />
              </div>
            );
          })
        )
      )}
      <div className="flex items-center gap-2">
        <CurrencySelector
          name={`${quote.id}-counter-currency`}
          label={t('rfq_view.currency_label')}
          value={draft.currency}
          onChange={(_, value) => patch({ currency: value as Currency })}
          disabled={orderedItems.size > 0}
        />
        <Input
          name={`${quote.id}-counter-lead-time`}
          aria-label={t('rfq_view.lead_time_label')}
          type="number"
          min={1}
          step="1"
          containerClassName="min-w-[90px]"
          value={draft.leadTimeDays}
          onChange={(_, value) => patch({ leadTimeDays: value })}
        />
      </div>
      <Button
        variant="primary"
        size="sm"
        width="auto"
        data-testid={`confirm-counter-${quote.id}`}
        onClick={() => void handleConfirm()}
        disabled={isSending}
        loading={isSending}
      >
        {t('rfq_view.counter_quote')}
      </Button>
    </div>
  );
}
